<template>
  <div class="mt-4">
    <CommunityCommentTree
      :comments="liveComments"
      :scheduledComments="scheduledComments"
      :user="currentUser"
      :entityOwnerId="communityProfileId"
      :entityType="entityType"
      :entityId="entityId"
      :additionalData="additionalData"
      :hashtagsEnabled="hashtagsEnabled"
      :hashtags="hashtags"
      :commentsAreLoading="isLoading"
      @onHashtag="onHashtag"
      @loadMoreComments="loadMoreComments"
    />
    <div v-if="isLoading" class="text-center w-100">
      <HMSpinner />
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import CommunityCommentTree from '@/web/components/community/CommunityCommentTree';
import { getStudioURL } from '@/web/router/custom-domain-utils';
import { errorToMessage } from '@/core/utils/errors';
import moment from 'moment';

export default Vue.extend({
  name: 'CommunityComments',
  components: {
    CommunityCommentTree,
  },
  props: {
    entityType: {
      type: String,
    },
    entityId: {
      type: Number,
    },
    additionalData: {
      type: Object,
    },
    hashtagsEnabled: {
      type: Boolean,
    },
    hashtags: {
      type: Array,
    },
  },
  computed: {
    ...mapGetters({
      communityProfileId: 'studio/communityProfileId',
      interactAsStudio: 'community/interactAsStudio',
      comments: 'community/comments',
    }),
    studioURL() {
      return getStudioURL(this.$route);
    },
    scheduledComments() {
      return this.comments.filter(c => moment(c.release_on).isAfter(moment()));
    },
    liveComments() {
      return this.comments.filter(
        c => !c.release_on || moment(c.release_on).isBefore(moment())
      );
    },
  },
  data() {
    return {
      isLoading: true,
      currentUser: {},
      hasMoreComments: false,
      page: 1,
    };
  },
  methods: {
    async loadData() {
      try {
        this.page = 1;
        this.isLoading = true;
        const commentIdParam = this.$route.query.comment;
        await this.$store.dispatch('community/loadComments', {
          entityType: this.entityType,
          entityId: this.entityId,
          entityOwnerId: this.communityProfileId,
          page: this.page,
          commentId: commentIdParam,
        });
        this.hasMoreComments = this.comments?.length > 0;
        if (commentIdParam) {
          setTimeout(() => {
            this.$nextTick(() => {
              const commentElement = document.getElementById(
                `comment-${commentIdParam}`
              );
              console.log('selected commentElement', commentElement);
              if (commentElement) {
                const yOffset = -60;
                const y =
                  commentElement.getBoundingClientRect().top +
                  window.pageYOffset +
                  yOffset;
                window.scrollTo({ top: y });
                console.log('scrolled to ', y);
              }
            });
          }, 200);
        }
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1,
        });
      } finally {
        this.isLoading = false;
      }
    },
    async loadMoreComments() {
      if (!this.hasMoreComments) return;
      try {
        this.page += 1;
        this.isLoading = true;
        const numberOfComments = this.comments.length;
        const commentIdParam = this.$route.query.comment;
        await this.$store.dispatch('community/loadComments', {
          entityType: this.entityType,
          entityId: this.entityId,
          entityOwnerId: this.communityProfileId,
          page: this.page,
          commentId: commentIdParam,
        });
        this.hasMoreComments = this.comments?.length > numberOfComments;
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1,
        });
      } finally {
        this.isLoading = false;
      }
    },
    async loadUserData() {
      this.isLoading = true;
      try {
        this.currentUser = await this.$store.dispatch(
          'community/loadMyCommunityProfileData'
        );
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(
            error.response,
            `${this.$t('COMMUNITY.TEXT.PROFILE_LOADING_ERROR')}: ${error}`
          ),
          duration: -1,
        });
      } finally {
        this.isLoading = false;
      }
    },
    onHashtag(hashtag) {
      this.$emit('onHashtag', hashtag);
    },
  },
  async mounted() {
    await this.loadUserData();
    await this.loadData();
  },
  watch: {
    async interactAsStudio(value) {
      await this.loadUserData();
      await this.loadData();
    },
    '$route.query.comment': {
      handler(comment) {
        console.log('comment_id: ', comment);
        this.loadData();
      },
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';
.comment__block {
  padding: 15px 20px 22px 32px;
  border: 1px solid #e3e7ec;
  border-radius: 6px;
  background-color: $white;
  position: relative;
}
</style>
